<template>
  <div class="chatbot-upload-page">
    <div class="upload-page-content">
      <PageHeader
        :title="selectedFile ? 'Re-upload Document' : 'Document Upload'"
        :breadcrumbs="candidateId ? [
          { label: 'Financial Assistant', to: { name: 'chatbot' }, icon: 'las la-home' },
          { label: selectedCandidate ? selectedCandidate.name : 'Candidate', to: { name: 'chatbot-candidate', params: { candidateId } } },
          { label: 'Upload' }
        ] : [
          { label: 'Financial Assistant', to: { name: 'chatbot' }, icon: 'las la-home' },
          { label: 'Upload' }
        ]"
      />

      <FileUpload
        @file-selected="handleFileUpload"
        @batch-files-selected="handleBatchFileUpload"
        @cancel="goBack"
        @pre-selected-file-cleared="clearSelectedFile"
        :scan-progress="uploadProgress"
        :pre-selected-file="selectedFile"
        :pre-selected-file-type="selectedFileType"
      />

      <div v-if="error" class="error-message">
        <i class="las la-exclamation-circle"></i>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from './components/FileUpload.vue'
import ChatbotService from "@/services/chatbot.service";
import PageHeader from './components/PageHeader.vue';
import { watchUploadProgress, watchBatchUploadProgress } from '@/services/upload-progress-socket';

export default {
  name: 'ChatbotUpload',
  components: {
    FileUpload,
    PageHeader
  },
  props: {
    preSelectedFile: {
      type: Object,
      default: null
    },
    candidateId: {
      type: String,
      required: false
    },
    preSelectedFileType: {
      type: String,
      default: null
    }
  },
  computed: {
    selectedFileType() {
      // Priority: prop > query param
      return this.preSelectedFileType || this.$route.query.file_type || null;
    }
  },
  data() {
    return {
      uploading: false,
      error: null,
      selectedFile: null,
      selectedCandidate: null,
      stopUploadProgressWatch: null,
      uploadProgress: {
        show: false,
        percentage: 0,
        status: 'processing',
        currentPage: 0,
        totalPages: 0,
        taskType: 'upload',
        message: '',
        file_name: '',
        upload_id: '',
        batchFiles: [],
        batchStats: null
      },
      batchFileMap: {},
      batchUploadFileType: null,
    }
  },
  mounted() {
    // Fetch candidate information
    this.fetchCandidateInfo();
    
    // Check for file in query parameters (from scan again button)
    if (this.$route.query.file) {
      try {
        const fileFromQuery = JSON.parse(this.$route.query.file);
        this.selectedFile = fileFromQuery;
      } catch (error) {
        console.error('Error parsing file from query:', error);
      }
    }
    
    // If a file is pre-selected via props, use it
    if (this.preSelectedFile) {
      this.selectedFile = this.preSelectedFile;
    }
  },
  beforeDestroy() {
    this.stopWatchingUploadProgress();
  },
  methods: {
    goBack() {
      this.stopWatchingUploadProgress();
      if (this.candidateId) {
        // Navigate back to the candidate sessions page
        this.$router.push({ 
          name: 'chatbot-candidate',
          params: { candidateId: this.candidateId }
        });
      } else {
        // Fall back to general chatbot page
        this.$router.push({ name: 'chatbot' });
      }
    },
    
    clearSelectedFile() {
      this.selectedFile = null;
      // Clear the query parameter
      this.$router.replace({ query: {} });
    },
    
    async fetchCandidateInfo() {
      if (!this.candidateId) {
        return; // No candidate ID, skip fetching
      }
      
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.selectedCandidate = candidate;
      } catch (error) {
        console.error('Error fetching candidate info:', error);
        this.error = 'Failed to load candidate information.';
      }
    },

    // Unified upload method that handles both new file uploads and pre-selected files
    async handleFileUpload(file, fileType, password = null) {
      this.uploading = true;
      this.error = null;
      
      try {
        let fileToUpload = file;
        
        // If we have a pre-selected file with file_url, create a proper File object
        if (this.selectedFile && this.selectedFile.file_url && !(file instanceof File)) {
          try {
            const response = await fetch(this.selectedFile.file_url);
            const blob = await response.blob();
            const correctFileName = this.selectedFile.file_name || this.selectedFile.name || 'document.pdf';
            const correctFileType = this.selectedFile.document_type || 'application/pdf';
            
            fileToUpload = new File([blob], correctFileName, {
              type: correctFileType
            });
          } catch (fetchError) {
            console.error('Error fetching file from URL:', fetchError);
            fileToUpload = file;
          }
        }
        
        // Upload the file (HTTP multipart). Processing progress comes over WebSocket.
        const data = await this.uploadFile(fileToUpload, fileType, password);
        console.log('Upload response:', data);

        const uploadId = data?.upload_id;
        const isProcessing = data?.status === 'processing'
          || data?.status === 'extracting'
          || data?.status === 'scanning'
          || data?.status === 'indexing'
          || data?.status === 'pending'
          || data?.type === 'upload_started';

        if (isProcessing && uploadId) {
          this.startWatchingUploadProgress(data, fileToUpload);
        } else if (data?.status === 'completed') {
          console.log('Upload completed:', data);
          this.handleUploadSuccess(data, fileToUpload);
        } else if (data?.status === 'failed') {
          this.handleUploadError(data);
        }
      } catch (err) {
        this.handleUploadError(err);
      }
      
      this.uploading = false;
    },

    startWatchingUploadProgress(uploadResponse, fileToUpload) {
      this.stopWatchingUploadProgress();

      const uploadId = uploadResponse.upload_id;
      this.uploadProgress.show = true;
      this.uploadProgress.percentage = 0;
      this.uploadProgress.status = uploadResponse.status || 'processing';
      this.uploadProgress.message = uploadResponse.message || 'File upload started';
      this.uploadProgress.file_name = uploadResponse.file_name || fileToUpload?.name || '';
      this.uploadProgress.upload_id = uploadId;
      this.uploadProgress.currentPage = 0;
      this.uploadProgress.totalPages = 0;
      this.uploadProgress.taskType = this.mapUploadStatusToTaskType(uploadResponse.status);
      this.uploadProgress.batchFiles = [];
      this.uploadProgress.batchStats = null;

      this.stopUploadProgressWatch = watchUploadProgress({
        websocketUrl: uploadResponse.websocket_url || `ws/uploads/${uploadId}/`,
        uploadId,
        onProgress: (progress) => {
          this.applyUploadProgress(progress);
        },
        onDone: (result) => {
          console.log('Upload success:', result);
          this.stopUploadProgressWatch = null;
          this.uploadProgress.show = false;
          this.handleUploadSuccess({ ...result, upload_id: uploadId }, fileToUpload);
        },
        onError: (error) => {
          console.log('Upload error:', error);
          this.stopUploadProgressWatch = null;
          this.uploadProgress.show = false;
          this.handleUploadError(error);
        }
      });
    },

    applyUploadProgress(progress) {
      this.uploadProgress.show = true;
      this.uploadProgress.percentage = Number.isFinite(Number(progress.progress))
        ? Number(progress.progress)
        : this.uploadProgress.percentage;
      this.uploadProgress.message = progress.message
        || this.getDefaultProgressMessage(progress.status);
      this.uploadProgress.currentPage = progress.current_page ?? this.uploadProgress.currentPage;
      this.uploadProgress.totalPages = progress.total_pages ?? this.uploadProgress.totalPages;
      this.uploadProgress.status = progress.status || this.uploadProgress.status;
      this.uploadProgress.taskType = this.mapUploadStatusToTaskType(progress.status);
      if (progress.file_name) {
        this.uploadProgress.file_name = progress.file_name;
      }
      if (progress.upload_id) {
        this.uploadProgress.upload_id = progress.upload_id;
      }
    },

    mapUploadStatusToTaskType(status) {
      switch (status) {
        case 'scanning':
          return 'scan';
        case 'extracting':
        case 'indexing':
          return 'processing';
        case 'pending':
        case 'processing':
        default:
          return 'upload';
      }
    },

    getDefaultProgressMessage(status) {
      switch (status) {
        case 'scanning':
          return 'Scanning file...';
        case 'extracting':
          return 'Extracting data...';
        case 'indexing':
          return 'Indexing file...';
        case 'pending':
          return 'Waiting to start...';
        case 'processing':
          return 'Processing file...';
        default:
          return 'Processing...';
      }
    },

    stopWatchingUploadProgress() {
      if (typeof this.stopUploadProgressWatch === 'function') {
        this.stopUploadProgressWatch();
      }
      this.stopUploadProgressWatch = null;
    },

    // Handle batch file upload
    async handleBatchFileUpload(files, fileType, password = null) {
      this.uploading = true;
      this.error = null;
      this.batchUploadFileType = fileType;
      
      try {
        console.log('Batch uploading files:', files);
        
        const data = await this.batchUploadFiles(files, fileType, password);
        console.log('Batch upload response:', data);
        
        if (data?.batch_id) {
          this.startWatchingBatchUploadProgress(data, files);
        } else {
          this.handleUploadError(new Error('No batch ID received from server'));
        }
      } catch (err) {
        this.handleUploadError(err);
      }
      
      this.uploading = false;
    },

    startWatchingBatchUploadProgress(batchResponse, files) {
      this.stopWatchingUploadProgress();

      const batchId = batchResponse.batch_id;
      const uploads = Array.isArray(batchResponse.uploads) ? batchResponse.uploads : [];
      const totalFiles = batchResponse.total_files || files.length;

      this.batchFileMap = this.buildBatchFileMap(uploads, files);
      this.uploadProgress.show = true;
      this.uploadProgress.percentage = 0;
      this.uploadProgress.status = 'pending';
      this.uploadProgress.message = batchResponse.message || 'Starting batch upload...';
      this.uploadProgress.taskType = 'batch-upload';
      this.uploadProgress.currentPage = 0;
      this.uploadProgress.totalPages = 0;
      this.uploadProgress.file_name = '';
      this.uploadProgress.upload_id = '';
      this.uploadProgress.batchStats = {
        total_files: totalFiles,
        processed_files: 0,
        successful_files: 0,
        failed_files: batchResponse.failed_uploads || 0,
        processing_files: 0,
        current_file_name: null,
        current_file_index: null,
        current_upload_id: null,
        status: 'pending'
      };
      this.syncBatchFilesToProgress();

      this.stopUploadProgressWatch = watchBatchUploadProgress({
        websocketUrl: batchResponse.websocket_url || `ws/batch-uploads/${batchId}/`,
        batchId,
        onBatchProgress: (progress) => {
          this.applyBatchUploadProgress(progress);
        },
        onFileProgress: (progress) => {
          this.applyBatchFileProgress(progress);
        },
        onDone: (result) => {
          this.stopUploadProgressWatch = null;
          this.handleBatchUploadSuccess(result, files);
        },
        onPartial: (result) => {
          this.stopUploadProgressWatch = null;
          this.handleBatchUploadSuccess(result, files);
        },
        onError: (error) => {
          this.stopUploadProgressWatch = null;
          this.uploadProgress.show = false;
          this.handleUploadError(error);
        }
      });
    },

    buildBatchFileMap(uploads, files) {
      const map = {};

      if (uploads.length > 0) {
        uploads.forEach((upload) => {
          const uploadId = upload.upload_id || `index-${upload.file_index}`;
          map[uploadId] = {
            upload_id: upload.upload_id || null,
            file_name: upload.file_name || `File ${(upload.file_index ?? 0) + 1}`,
            file_index: upload.file_index ?? 0,
            status: upload.status === 'failed' ? 'failed' : 'pending',
            progress: 0,
            current_page: 0,
            total_pages: 0,
            message: upload.message || (upload.status === 'failed' ? 'Upload failed' : 'Waiting to start...'),
            error: upload.status === 'failed' ? (upload.message || 'Upload failed') : null,
            is_current: false
          };
        });
        return map;
      }

      files.forEach((file, index) => {
        const uploadId = `index-${index}`;
        map[uploadId] = {
          upload_id: null,
          file_name: file.name,
          file_index: index,
          status: 'pending',
          progress: 0,
          current_page: 0,
          total_pages: 0,
          message: 'Waiting to start...',
          error: null,
          is_current: false
        };
      });

      return map;
    },

    syncBatchFilesToProgress() {
      const currentUploadId = this.uploadProgress.batchStats?.current_upload_id || null;

      this.uploadProgress.batchFiles = Object.values(this.batchFileMap)
        .map((file) => ({
          ...file,
          is_current: !!(
            currentUploadId
            && file.upload_id
            && file.upload_id === currentUploadId
          )
        }))
        .sort((a, b) => (a.file_index ?? 0) - (b.file_index ?? 0));
    },

    applyBatchUploadProgress(progress) {
      this.uploadProgress.show = true;
      this.uploadProgress.taskType = 'batch-upload';
      this.uploadProgress.status = progress.status || this.uploadProgress.status;
      this.uploadProgress.percentage = Number.isFinite(Number(progress.progress))
        ? Number(progress.progress)
        : this.uploadProgress.percentage;

      const totalFiles = progress.total_files
        ?? this.uploadProgress.batchStats?.total_files
        ?? this.uploadProgress.batchFiles.length;
      const processedFiles = progress.processed_files ?? 0;
      const successfulFiles = progress.successful_files ?? 0;
      const failedFiles = progress.failed_files ?? 0;
      const processingFiles = progress.processing_files ?? 0;
      const currentFileName = progress.current_file_name || null;
      const currentFileIndex = progress.current_file_index;
      const currentUploadId = progress.current_upload_id || null;

      this.uploadProgress.batchStats = {
        total_files: totalFiles,
        processed_files: processedFiles,
        successful_files: successfulFiles,
        failed_files: failedFiles,
        processing_files: processingFiles,
        current_file_name: currentFileName,
        current_file_index: currentFileIndex,
        current_upload_id: currentUploadId,
        status: progress.status || 'processing'
      };

      if (progress.message) {
        this.uploadProgress.message = progress.message;
      } else if (currentFileName) {
        const indexLabel = Number.isInteger(currentFileIndex)
          ? ` (${currentFileIndex + 1}/${totalFiles})`
          : '';
        this.uploadProgress.message = `Processing "${currentFileName}"${indexLabel}… ${processedFiles}/${totalFiles} processed`;
      } else {
        this.uploadProgress.message = `Processing files… ${processedFiles}/${totalFiles} processed`;
      }

      if (failedFiles > 0 && !String(this.uploadProgress.message).toLowerCase().includes('fail')) {
        this.uploadProgress.message += ` · ${failedFiles} failed`;
      }

      // Soft-highlight current file from batch event if we know its id.
      if (currentUploadId && this.batchFileMap[currentUploadId]) {
        this.batchFileMap = {
          ...this.batchFileMap,
          [currentUploadId]: {
            ...this.batchFileMap[currentUploadId],
            file_name: currentFileName || this.batchFileMap[currentUploadId].file_name,
            status: ['completed', 'failed'].includes(this.batchFileMap[currentUploadId].status)
              ? this.batchFileMap[currentUploadId].status
              : 'processing'
          }
        };
      }

      this.syncBatchFilesToProgress();
    },

    applyBatchFileProgress(progress) {
      const uploadId = progress.upload_id;
      if (!uploadId) return;

      const existing = this.batchFileMap[uploadId] || {
        upload_id: uploadId,
        file_name: progress.file_name || 'File',
        file_index: Object.keys(this.batchFileMap).length,
        status: 'pending',
        progress: 0,
        current_page: 0,
        total_pages: 0,
        message: '',
        error: null,
        is_current: false
      };

      const status = progress.status || existing.status;
      const isFailed = status === 'failed' || status === 'error';

      this.batchFileMap = {
        ...this.batchFileMap,
        [uploadId]: {
          ...existing,
          upload_id: uploadId,
          file_name: progress.file_name || existing.file_name,
          status,
          progress: Number.isFinite(Number(progress.progress))
            ? Number(progress.progress)
            : existing.progress,
          current_page: progress.current_page ?? existing.current_page,
          total_pages: progress.total_pages ?? existing.total_pages,
          message: isFailed
            ? (progress.error || progress.message || 'Failed')
            : (progress.message || existing.message),
          error: isFailed ? (progress.error || progress.message || 'Failed') : null
        }
      };

      this.syncBatchFilesToProgress();
    },

    async uploadFile(file, fileType, password = null) {
      if (!this.candidateId) {
        throw new Error('Candidate ID is required for file uploads');
      }
      return ChatbotService.uploadFile(this.candidateId, file, fileType, password);
    },


    async batchUploadFiles(files, fileType, password = null) {
      if (!this.candidateId) {
        throw new Error('Candidate ID is required for batch uploads');
      }
      return ChatbotService.batchUploadFiles(this.candidateId, files, fileType, password);
    },


    handleUploadSuccess(data, file) {
      this.hideProgressBar();

      const fileType = this.selectedFileType
        || this.$route.query.type
        || file?.document_type
        || file?.type
        || null;

      if (this.candidateId && fileType === 'erp_extract') {
        this.$router.push({
          name: 'chatbot-candidate-overview',
          params: { candidateId: this.candidateId }
        });
        return;
      }

      const fileId = data?.id || data?.file_id || data?.upload_id;

      if (this.candidateId && fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: {
            candidateId: this.candidateId,
            fileId: String(fileId)
          }
        });
        return;
      }

      if (this.candidateId) {
        this.$router.push({
          name: 'chatbot-candidate-files',
          params: { candidateId: this.candidateId }
        });
      } else {
        this.$router.push({ name: 'chatbot' });
      }
    },

    handleBatchUploadSuccess(data, files) {
      this.hideProgressBar();

      const successful = data.successful_files ?? data.completed_uploads ?? 0;
      const failed = data.failed_files ?? data.failed_uploads ?? 0;
      const total = data.total_files || files.length;
      const isPartial = data.status === 'partially_failed' || failed > 0;

      let message = `Batch upload completed! ${successful}/${total} files uploaded successfully.`;
      if (failed > 0) {
        message += ` ${failed} file${failed === 1 ? '' : 's'} failed.`;
      }

      this.$bvToast.toast(message, {
        title: isPartial ? 'Batch Upload Partially Complete' : 'Batch Upload Complete',
        variant: isPartial ? 'warning' : 'success',
        solid: true,
        autoHideDelay: 5000
      });

      if (this.candidateId) {
        this.$router.push({
          name: this.getBatchUploadRedirectRoute(),
          params: { candidateId: this.candidateId }
        });
      } else {
        this.$router.push({ name: 'chatbot' });
      }

      this.batchUploadFileType = null;
    },

    getBatchUploadRedirectRoute() {
      if (this.batchUploadFileType === 'vat_statement') {
        return 'chatbot-candidate-vat';
      }

      if (this.batchUploadFileType === 'bank_statement'
        || this.batchUploadFileType === 'multi_account_statement') {
        return 'chatbot-candidate-bank-statement';
      }

      return 'chatbot-candidate-files';
    },

    handleUploadError(err) {
      this.stopWatchingUploadProgress();
      const detail = err?.error || err?.message || (typeof err === 'string' ? err : null);
      this.error = detail
        ? `Error uploading file: ${detail}`
        : 'Error uploading file. Please try again.';
      this.uploadProgress.show = false;
      this.uploadProgress.batchStats = null;
      this.batchFileMap = {};
    },

    hideProgressBar() {
      this.uploadProgress.show = false;
      this.uploadProgress.batchStats = null;
      this.batchFileMap = {};
    },


  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.chatbot-upload-page {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.upload-page-content {
  flex: 1;
  width: 100%;
  padding: 0 2rem 3rem;
  box-sizing: border-box;
}

.upload-page-content ::v-deep .page-header {
  padding-left: 0;
  padding-right: 0;
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  background: #fed7d7;
  color: #e53e3e;
  font-size: 0.9rem;
  font-family: $brand-font-regular;

  i {
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-top: 0.05rem;
  }
}

@media (max-width: 768px) {
  .upload-page-content {
    padding: 0 1rem 2rem;
  }
}
</style> 